import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authFusionService from '../services/authfusion';
import { CheckCircle, XCircle } from 'lucide-react';

const AuthCallback = () => {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [processed, setProcessed] = useState(false);
  const { handleCallback, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (processed) return;

    if (user) {
      setStatus('success');
      setTimeout(() => navigate('/', { replace: true }), 500);
      return;
    }

    const processCallback = async () => {
      try {
        setStatus('processing');
        setProcessed(true);
        await handleCallback();
        setStatus('success');
        setTimeout(() => navigate('/', { replace: true }), 500);
      } catch (callbackError) {
        if (callbackError.message && callbackError.message.includes('No matching state')) {
          const currentUser = await authFusionService.getUser();
          if (currentUser && !currentUser.expired) {
            setStatus('success');
            setTimeout(() => navigate('/', { replace: true }), 500);
            return;
          }
        }

        setError(callbackError.message || 'Authentication failed');
        setStatus('error');
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    processCallback();
  }, [handleCallback, navigate, processed, user]);

  const renderContent = () => {
    if (status === 'processing') {
      return (
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-brand-900 mb-2">Completing Authentication</h2>
          <p className="text-brand-700">Please wait while we complete your sign-in...</p>
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-brand-900 mb-2">Authentication Successful</h2>
          <p className="text-brand-700">Redirecting you to the dashboard...</p>
        </div>
      );
    }

    return (
      <div className="text-center">
        <div className="mx-auto h-12 w-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <XCircle className="h-8 w-8 text-red-600" />
        </div>
        <h2 className="text-xl font-semibold text-brand-900 mb-2">Authentication Failed</h2>
        <p className="text-brand-700 mb-4">{error || 'An error occurred during authentication'}</p>
        <p className="text-sm text-brand-600">Redirecting you to the login page...</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 via-brand-100 to-brand-200 p-4">
      <div className="max-w-md w-full">
        <div className="card">{renderContent()}</div>
      </div>
    </div>
  );
};

export default AuthCallback;
